"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SignedTransaction = exports.Secp256k1Credentials = exports.TransactionCredentials = exports.AbstractSignable = void 0;
const utils_1 = require("../utils");
const constants_1 = require("./constants");
const types_1 = require("./types");
class AbstractSignable {
    async sign(bytes, signers) {
        const addresses = this.getAddresses();
        const signatures = [];
        for (let i = 0; i < this.getThreshold() && i < addresses.length; i++) {
            const address = addresses[i];
            for (const signer of signers) {
                if (signer.matches(address)) {
                    const signature = await signer.sign(bytes);
                    signatures.push(new types_1.Signature(signature));
                    break;
                }
            }
        }
        return signatures;
    }
}
exports.AbstractSignable = AbstractSignable;
class TransactionCredentials {
    typeId;
    signatures;
    constructor(typeId, signatures) {
        this.typeId = typeId;
        this.signatures = signatures;
    }
    serialize() {
        const buffer = utils_1.JuneoBuffer.alloc(
        // 4 + 4 = 8
        8 + constants_1.SignatureSize * this.signatures.length);
        buffer.writeUInt32(this.typeId);
        buffer.writeUInt32(this.signatures.length);
        for (const signature of this.signatures) {
            buffer.write(signature);
        }
        return buffer;
    }
    static parse(data) {
        const buffer = utils_1.JuneoBuffer.from(data);
        const reader = buffer.createReader();
        const typeId = reader.readUInt32();
        const signaturesLength = reader.readUInt32();
        const signatures = [];
        for (let i = 0; i < signaturesLength; i++) {
            signatures.push(new types_1.Signature(reader.read(constants_1.SignatureSize)));
        }
        switch (typeId) {
            case constants_1.Secp256k1CredentialsTypeId: {
                return new Secp256k1Credentials(signatures);
            }
            default: {
                throw new utils_1.ParsingError(`unsupported credentials type id "${typeId}"`);
            }
        }
    }
    static comparator = (a, b) => {
        return utils_1.JuneoBuffer.comparator(a.serialize(), b.serialize());
    };
}
exports.TransactionCredentials = TransactionCredentials;
class Secp256k1Credentials extends TransactionCredentials {
    constructor(signatures) {
        super(constants_1.Secp256k1CredentialsTypeId, signatures);
    }
}
exports.Secp256k1Credentials = Secp256k1Credentials;
class SignedTransaction {
    unsignedTransaction;
    credentials = [];
    constructor(unsignedTransaction, signatures) {
        this.unsignedTransaction = unsignedTransaction;
        if (signatures.length > 0) {
            this.buildCredentials(signatures);
        }
    }
    getTransactionId() {
        return (0, utils_1.sha256)(this.serialize()).toCB58();
    }
    /**
     * Verify if the signed transaction has enough signatures to be sent over the network.
     *
     * We expect that each address that has signed the transaction has effectively signed
     * all of the Signable components of it that they match. That way we can verify credentials
     * faster and more easily, as well as identifying missing ones. This should always be
     * the case if signatures functions are called within regular usage of juneojs.
     *
     * @return Address[] List of addresses of which at least a signature is missing.
     * If no signature is missing, then an empty list is returned.
     */
    verifySignatures() {
        const missing = [];
        const signablesAddresses = this.getSignablesUniqueAddresses();
        const credentialsAddresses = this.getCredentialsUniqueAddresses();
        for (const address of signablesAddresses) {
            if (!address.matchesList(credentialsAddresses) && !address.matchesList(missing)) {
                missing.push(address);
            }
        }
        return missing;
    }
    getSignablesUniqueAddresses() {
        const addresses = [];
        for (const signable of this.unsignedTransaction.getSignables()) {
            addresses.push(...signable.getAddresses());
        }
        return types_1.Address.uniqueAddresses(addresses);
    }
    getCredentialsUniqueAddresses() {
        const addresses = [];
        const message = this.unsignedTransaction.serialize();
        for (const credentials of this.credentials) {
            for (const signature of credentials.signatures) {
                addresses.push(signature.recoverAddress(message));
            }
        }
        return types_1.Address.uniqueAddresses(addresses);
    }
    buildCredentials(signatures) {
        const message = this.unsignedTransaction.serialize();
        const credentials = [];
        for (const signable of this.unsignedTransaction.getSignables()) {
            const sigs = this.getMatchingSignatures(signable, signatures, message);
            credentials.push(new Secp256k1Credentials(sigs));
        }
        this.credentials = credentials;
    }
    getMatchingSignatures(signable, signatures, message) {
        const sigs = [];
        for (const address of signable.getAddresses()) {
            for (const signature of signatures) {
                const signatureAddress = signature.recoverAddress(message);
                if (address.matches(signatureAddress)) {
                    sigs.push(signature);
                    break;
                }
            }
            if (sigs.length >= signable.getThreshold()) {
                break;
            }
        }
        return sigs;
    }
    serialize() {
        const bytes = this.unsignedTransaction.serialize();
        const credentials = [];
        let credentialsSize = 0;
        for (const credential of this.credentials) {
            const credentialBytes = credential.serialize();
            credentialsSize += credentialBytes.length;
            credentials.push(credentialBytes);
        }
        const buffer = utils_1.JuneoBuffer.alloc(bytes.length + 4 + credentialsSize);
        buffer.write(bytes);
        buffer.writeUInt32(credentials.length);
        for (const credential of credentials) {
            buffer.write(credential);
        }
        return buffer;
    }
    static parse(data) {
        const buffer = utils_1.JuneoBuffer.from(data);
        const reader = buffer.createReader();
        const unsignedTransaction = utils_1.TransactionUtils.parseUnsignedTransaction(data);
        reader.skip(unsignedTransaction.serialize().length);
        const credentials = [];
        const credentialsLength = reader.readUInt32();
        for (let i = 0; i < credentialsLength; i++) {
            const credential = TransactionCredentials.parse(reader.peekRemaining());
            reader.skip(credential);
            credentials.push(credential);
        }
        const signedTransaction = new SignedTransaction(unsignedTransaction, []);
        signedTransaction.credentials = credentials;
        return signedTransaction;
    }
}
exports.SignedTransaction = SignedTransaction;
